import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useStockData from "../hooks/useStockData";
import ProductModal from "./ProductModal";
import { useTheme } from "../context/ThemeContext";
import { TextField, IconButton, MenuItem } from "@mui/material";
import { AddCircle, Delete } from "@mui/icons-material";
import { Autocomplete } from "@mui/material";

const EditOrderModal = ({ isOpen, onClose, order, updateOrder }) => {
  const { allProducts, refreshStockData } = useStockData();
  const { darkMode } = useTheme();
  const [editedOrder, setEditedOrder] = useState(() => {
    if (!order) return { orderItems: [], customerSource: null };

    return {
      ...order,
      customerSource: order.customerSource === 0 ? null : order.customerSource,
      orderItems: Array.isArray(order.items)
        ? order.items.map((item) => ({
            ...item,
            isManualMP: item.isManualMP ?? false,
          }))
        : [],
    };
  });

  const [selectedProductIndex, setSelectedProductIndex] = useState(null);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false); // 🔥 Termék modal nyitás/zárás
  const [newProduct, setNewProduct] = useState({});
  const [error, setError] = useState(""); // ❌ Hibaüzenet állapot
  const [successMessage, setSuccessMessage] = useState(""); // ✅ Sikerüzenet state
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [preferentialPrice, setPreferentialPrice] = useState(0);
  const [lastModifiedField, setLastModifiedField] = useState(null);
  const [tva, setTva] = useState(0);
  const [totalWithTVA, setTotalWithTVA] = useState(0);

  const [totalPrice, setTotalPrice] = useState(0);

  const totalML = editedOrder.orderItems
    .reduce((acc, item) => acc + (parseFloat(item.quantityML) || 0), 0)
    .toFixed(4);

  useEffect(() => {
    const customerType = order?.customer?.type ?? 0;

    const total = editedOrder.orderItems.reduce((acc, item) => {
      const mp = parseFloat(item.quantityMP) || 0;
      const ml = parseFloat(item.quantityML) || 0;
      const buc = parseFloat(item.quantityBUC) || 0; // ➡️ ADD THIS

      const product = allProducts.find((p) => p.id === item.productId);
      if (!product) return acc;

      const price = customerType === 0 ? product.price : product.priceMarmurist;

      // 🔥 Most már rendesen priorizáljuk: ha ML van, az a fontos, ha MP van az, ha csak BUC → akkor az
      let itemTotal = 0;
      if (ml > 0) itemTotal = price * ml;
      else if (mp > 0) itemTotal = price * mp;
      else if (buc > 0) itemTotal = price * buc; // ➡️ EZ HIÁNYZOTT

      return acc + itemTotal;
    }, 0);

    const tvaValue = parseFloat((total * 0.21).toFixed(2));
    const gross = parseFloat((total + tvaValue).toFixed(2));

    setTotalPrice(parseFloat(total.toFixed(2))); // nettó
    setTva(tvaValue); // TVA
    setTotalWithTVA(gross); // bruttó
  }, [editedOrder.orderItems, allProducts, order]);

  useEffect(() => {
    if (!totalPrice || totalPrice === 0) return;

    if (lastModifiedField === "preferentialPrice") {
      const netPreferential = preferentialPrice / 1.21;
      const discount = 100 - (netPreferential * 100) / totalPrice;
      setDiscountPercentage(parseFloat(discount.toFixed(2)));
    } else if (lastModifiedField === "discountPercentage") {
      const netAfterDiscount = totalPrice * (1 - discountPercentage / 100);
      const gross = netAfterDiscount * 1.21;
      setPreferentialPrice(parseFloat(gross.toFixed(2)));
    }
  }, [preferentialPrice, discountPercentage, totalPrice, lastModifiedField]);

  useEffect(() => {
    if (order && isOpen) {
      setEditedOrder({
        ...order,
        customerSource: order.customerSource === 0 ? null : order.customerSource,
        orderItems: Array.isArray(order.items)
          ? order.items.map((item) => ({
              ...item,
              isManualMP: item.isManualMP ?? false, // ha nincs, állítsd false-ra
            }))
          : [],
      });

      // 🔥 Preferențial price és discount betöltése, ha van
      setPreferentialPrice(order.preferentialPrice || 0);
      setDiscountPercentage(order.discountPercentage || 0);

      // 🔁 Szinkronizációhoz szükséges
      setLastModifiedField("preferentialPrice");

      // Egyéb állapotok resetelése
      setSelectedProductIndex(null);
      setError("");
      setSuccessMessage("");
      refreshStockData();
    }
  }, [order, isOpen]);

  useEffect(() => {
    if (totalPrice > 0 && preferentialPrice === 0) {
      setPreferentialPrice(totalPrice);
    }
  }, [totalPrice, preferentialPrice]);

  const totalMP = editedOrder.orderItems
    .reduce((acc, item) => acc + (parseFloat(item.quantityMP) || 0), 0)
    .toFixed(4);

  const handleItemChange = (index, field, value) => {
    setEditedOrder((prev) => {
      const updatedItems = [...prev.orderItems];
      const productId = updatedItems[index].productId;
      const product = allProducts.find((p) => p.id === productId);

      const newValue = field.includes("quantity")
        ? parseFloat(value) || 0
        : value;

      if (field === "quantityMP") {
        updatedItems[index] = {
          ...updatedItems[index],
          quantityMP: newValue,
          isManualMP: true,
          length: "",
          width: "",
          quantity: "",
        };
      } else if (["length", "width", "quantity"].includes(field)) {
        updatedItems[index] = {
          ...updatedItems[index],
          [field]: newValue,
          isManualMP: false, // automatikus számolás visszaáll
        };

        const length = parseFloat(updatedItems[index].length) || 0;
        const width = parseFloat(updatedItems[index].width) || 0;
        const quantity = parseInt(updatedItems[index].quantity) || 0;

        if (length > 0 && width > 0 && quantity > 0) {
          const computedMP = ((length * width * quantity) / 10000).toFixed(4);
          updatedItems[index].quantityMP = parseFloat(computedMP);
        } else {
          updatedItems[index].quantityMP = 0;
        }
      } else {
        updatedItems[index] = {
          ...updatedItems[index],
          [field]: newValue,
        };
      }

      return { ...prev, orderItems: updatedItems };
    });
  };

  const handleAddRow = () => {
    setEditedOrder((prev) => ({
      ...prev,
      orderItems: [
        ...prev.orderItems,
        {
          productId: "",
          materialName: "",
          length: "",
          width: "",
          quantity: "",
          quantityMP: "",
          quantityML: "",
          bizot: "",
          isManualMP: false,
        },
      ],
    }));
  };

  const handleRemoveItem = (index) => {
    setEditedOrder((prev) => ({
      ...prev,
      orderItems: prev.orderItems.filter((_, i) => i !== index),
    }));
    setError(""); // ✅ Hibaüzenet törlése
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setEditedOrder((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!order || !Array.isArray(order.items)) {
      setError("❌ Comanda nu este validă!");
      return;
    }

    for (const item of editedOrder.orderItems) {
      const mp = parseFloat(item.quantityMP) || 0;
      const ml = parseFloat(item.quantityML) || 0;
      const buc = parseFloat(item.quantityBUC) || 0;

      if (mp <= 0 && ml <= 0 && buc <= 0) {
        setError("❌ Trebuie completat MP, ML sau BUC!");
        return;
      }

      if (
        item.quantityBUC !== "" &&
        item.quantityBUC != null &&
        !Number.isInteger(Number(item.quantityBUC))
      ) {
        setError("❌ Cantitatea BUC trebuie să fie un număr întreg!");
        return;
      }
    }

    setError(""); // ✅ Ha minden rendben van, töröljük a hibaüzenetet
    const success = await updateOrder(
      {
        ...editedOrder,
        discountPercentage,
        preferentialPrice,
        id: order.id,
      },
      true
    );

    if (!success) {
      setError("❌ Nu s-a putut salva comanda. Verifică stocul sau datele!");
      return;
    }

    // 🔥 **Frissítjük a készletet**
    await refreshStockData();

    setSuccessMessage("✅ Modificările au fost salvate cu succes!");
    setTimeout(() => {
      setSuccessMessage("");
      onClose();
    }, 2000);
  }, [editedOrder, updateOrder, onClose, refreshStockData]);

  const handleAddProduct = () => {
    if (!newProduct.productId) {
      alert("Te rugăm să selectezi un produs!");
      return;
    }

    setEditedOrder((prev) => ({
      ...prev,
      orderItems: [...prev.orderItems, { ...newProduct }],
    }));

    // 🔥 Mindig állítsd vissza egy érvényes alapállapotra
    setNewProduct({
      productId: null, // Alapértelmezett érték
      quantityMP: 0,
      quantityML: 0,
      bizot: "",
    });

    setIsProductModalOpen(false);
  };

  return (
    isOpen && (
      <div
        className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 px-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          className={`p-6 rounded-lg shadow-xl w-[65%] max-w-7xl max-h-[85vh] overflow-y-auto relative transition-colors ${
            darkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {successMessage && (
            <div
              className={`absolute top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg shadow-md ${
                darkMode ? "bg-green-700 text-white" : "bg-green-500 text-white"
              }`}
            >
              {successMessage}
            </div>
          )}

          {error && (
            <div
              className={`absolute top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg shadow-md ${
                darkMode ? "bg-red-700 text-white" : "bg-red-600 text-white"
              }`}
            >
              {error}
            </div>
          )}

          <button
            onClick={onClose}
            className={`absolute top-4 right-5 text-3xl ${
              darkMode
                ? "text-red-400 hover:text-red-600"
                : "text-red-600 hover:text-red-800"
            }`}
          >
            ✖
          </button>

          <h2
            className={`text-2xl font-semibold mb-6 text-center ${
              darkMode ? "text-blue-400" : "text-blue-600"
            }`}
          >
            Modificare Comandă #{editedOrder.orderNumber || ""}
          </h2>

          <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
            <div>
              <label
                className={`block font-semibold mb-1 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Strada
              </label>
              <input
                type="text"
                name="street"
                value={editedOrder.street || ""}
                onChange={handleChange}
                className={`w-full p-2 border rounded-2xl ${
                  darkMode
                    ? "border-gray-600 bg-gray-800 text-white"
                    : "border-gray-300 bg-white text-black"
                }`}
              />
            </div>

            {/* Sursa clientului */}
            <div className="flex items-center gap-4">
              <label className={`font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                Sursa clientului:
              </label>
              {[{value: 1, label: "Online"}, {value: 2, label: "Recomandare"}, {value: 3, label: "Recurent"}].map(opt => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="customerSource"
                    value={opt.value}
                    checked={editedOrder.customerSource === opt.value}
                    onChange={() => setEditedOrder(prev => ({ ...prev, customerSource: opt.value }))}
                  />
                  <span className={darkMode ? "text-white" : "text-black"}>{opt.label}</span>
                </label>
              ))}

            </div>

            <h3
              className={`text-lg font-semibold mt-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Produse
            </h3>

            <div className="overflow-x-auto">
              <table
                className={`w-full border shadow-lg text-sm ${
                  darkMode
                    ? "border-gray-700 bg-gray-900 text-gray-300"
                    : "border-gray-300 bg-white text-gray-900"
                }`}
              >
                <thead>
                  <tr
                    className={`${
                      darkMode
                        ? "bg-gray-700 text-white"
                        : "bg-gray-200 text-gray-900"
                    }`}
                  >
                    <th className="border p-2 w-[15%]">Material</th>
                    <th className="border p-2 w-[8%]">Lungime</th>
                    <th className="border p-2 w-[8%]">Latime</th>
                    <th className="border p-2 w-[8%]">Bucăți</th>
                    <th className="border p-2 w-[10%]">MP</th>
                    <th className="border p-2 w-[8%]">ML</th>
                    <th className="border p-2 w-[8%]">BUC</th>
                    <th className="border p-2 w-[23%]">Bizot</th>
                    <th className="border p-2 w-[5%]">Acțiuni</th>
                  </tr>
                </thead>

                <tbody>
                  {editedOrder.orderItems.map((item, index) => {
                    const product = allProducts.find(
                      (p) => p.id === item.productId
                    );
                    const unitType = product?.unitType;

                    return (
                      <tr
                        key={index}
                        className={`hover:bg-opacity-60 transition ${
                          darkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"
                        }`}
                      >
                        <td className="border p-1 min-w-[350px] max-w-[500px]">
                          <Autocomplete
                            options={allProducts}
                            getOptionLabel={(option) => option.name}
                            value={
                              allProducts.find(
                                (p) => p.id === item.productId
                              ) || null
                            }
                            onChange={(event, newValue) => {
                              handleItemChange(
                                index,
                                "productId",
                                newValue?.id || ""
                              );
                              handleItemChange(
                                index,
                                "materialName",
                                newValue?.name || ""
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Selectează materialul"
                                variant="standard"
                                size="small"
                                InputProps={{
                                  ...params.InputProps,
                                  disableUnderline: true,
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  style: { textAlign: "center" },
                                }}
                              />
                            )}
                            sx={{
                              "& .MuiAutocomplete-inputRoot": {
                                paddingLeft: "6px",
                                textAlign: "center",
                              },
                            }}
                            slotProps={{
                              popper: {
                                modifiers: [
                                  {
                                    name: "offset",
                                    options: {
                                      offset: [0, 4],
                                    },
                                  },
                                  {
                                    name: "width",
                                    enabled: true,
                                    phase: "beforeWrite",
                                    requires: ["computeStyles"],
                                    fn({ state }) {
                                      state.styles.popper.width = "320px"; // vagy "max-content"
                                    },
                                  },
                                ],
                              },
                            }}
                          />
                        </td>

                        {/* 🔥 Új oszlopok: Hossz, Szélesség, Darabszám */}
                        <td className="border p-1">
                          <TextField
                            type="number"
                            size="small"
                            variant="standard"
                            value={item.length || ""}
                            onChange={(e) =>
                              handleItemChange(index, "length", e.target.value)
                            }
                            inputProps={{ style: { textAlign: "center" } }}
                            InputProps={{ disableUnderline: true }}
                            disabled={unitType === "BUC"}
                          />
                        </td>
                        <td className="border p-1">
                          <TextField
                            type="number"
                            size="small"
                            variant="standard"
                            value={item.width || ""}
                            onChange={(e) =>
                              handleItemChange(index, "width", e.target.value)
                            }
                            inputProps={{ style: { textAlign: "center" } }}
                            InputProps={{ disableUnderline: true }}
                            disabled={unitType === "BUC"}
                          />
                        </td>
                        <td className="border p-1">
                          <TextField
                            type="number"
                            size="small"
                            variant="standard"
                            value={item.quantity || ""}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "quantity",
                                e.target.value
                              )
                            }
                            inputProps={{ style: { textAlign: "center" } }}
                            InputProps={{ disableUnderline: true }}
                            disabled={unitType === "BUC"}
                          />
                        </td>

                        {/* 🔥 Az MP érték kézi megadása vagy automatikus számolása */}
                        <td className="border p-1">
                          <TextField
                            type="number"
                            size="small"
                            variant="standard"
                            value={item.quantityMP || ""}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "quantityMP",
                                e.target.value
                              )
                            }
                            inputProps={{ style: { textAlign: "center" } }}
                            InputProps={{ disableUnderline: true }}
                            disabled={unitType === "BUC"}
                          />
                        </td>

                        <td className="border p-1">
                          <TextField
                            type="number"
                            size="small"
                            variant="standard"
                            value={item.quantityML || ""}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "quantityML",
                                e.target.value
                              )
                            }
                            inputProps={{ style: { textAlign: "center" } }}
                            InputProps={{ disableUnderline: true }}
                            disabled={unitType === "BUC"}
                          />
                        </td>
                        <td className="border p-1">
                          <TextField
                            type="number"
                            size="small"
                            variant="standard"
                            value={item.quantityBUC || ""}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "quantityBUC",
                                e.target.value
                              )
                            }
                            inputProps={{ style: { textAlign: "center" } }}
                            InputProps={{ disableUnderline: true }}
                            disabled={unitType === "MP" || unitType === "ML"}
                          />
                        </td>

                        <td className="border p-1">
                          <TextField
                            type="text"
                            size="small"
                            variant="standard"
                            value={item.bizot || ""}
                            onChange={(e) =>
                              handleItemChange(index, "bizot", e.target.value)
                            }
                            inputProps={{ style: { textAlign: "center" } }}
                            InputProps={{ disableUnderline: true }}
                            fullWidth
                            multiline
                          />
                        </td>

                        <td className="border p-1 text-center">
                          <IconButton onClick={() => handleRemoveItem(index)}>
                            <Delete color="error" />
                          </IconButton>
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td
                      colSpan={6}
                      className="text-right font-semibold p-2 pr-10"
                    >
                      <span className="mr-10">
                        <span className="font-bold">MP Total:</span> {totalMP}
                      </span>
                      <span>
                        <span className="font-bold">ML Total:</span> {totalML}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={handleAddRow}
                  className={`flex items-center gap-2 px-4 py-2 rounded-2xl font-semibold transition ${
                    darkMode
                      ? "bg-blue-700 hover:bg-blue-600 text-white"
                      : "bg-blue-600 hover:bg-blue-500 text-white"
                  }`}
                >
                  <AddCircle />
                  Adaugă rând nou
                </button>
              </div>
            </div>
            {/* <button
              type="button"
              onClick={() => setIsProductModalOpen(true)}
              className={`w-full py-3 rounded-2xl transition ${
                darkMode
                  ? "bg-blue-700 hover:bg-blue-600 text-white"
                  : "bg-blue-600 hover:bg-blue-500 text-white"
              }`}
            >
              + Adaugă produs
            </button> */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label
                  className={`block font-semibold mb-1 ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Total brut (TVA inclus, fără discount)
                </label>
                <input
                  type="number"
                  value={totalWithTVA}
                  disabled
                  className={`w-full p-2 rounded-xl ${
                    darkMode
                      ? "bg-gray-800 text-white"
                      : "bg-gray-100 text-black"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block font-semibold mb-1 ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Preț Preferențial
                </label>
                <input
                  type="number"
                  value={preferentialPrice}
                  onChange={(e) => {
                    setPreferentialPrice(parseFloat(e.target.value));
                    setLastModifiedField("preferentialPrice");
                  }}
                  className={`w-full p-2 border rounded-xl ${
                    darkMode
                      ? "bg-gray-800 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-black"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block font-semibold mb-1 ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Discount (%)
                </label>
                <input
                  type="number"
                  value={discountPercentage}
                  onChange={(e) => {
                    setDiscountPercentage(parseFloat(e.target.value));
                    setLastModifiedField("discountPercentage");
                  }}
                  className={`w-full p-2 border rounded-xl ${
                    darkMode
                      ? "bg-gray-800 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-black"
                  }`}
                />
              </div>
            </div>

            <button
              type="button"
              onClick={handleSave}
              disabled={!!error}
              className={`w-full py-3 rounded-2xl transition ${
                error
                  ? "bg-gray-500 cursor-not-allowed"
                  : darkMode
                  ? "bg-green-700 hover:bg-green-600 text-white"
                  : "bg-green-600 hover:bg-green-500 text-white"
              }`}
            >
              Salvează Modificările
            </button>
          </form>
          {/* 📦 Termék kiválasztó modal */}
          {isProductModalOpen && (
            <ProductModal
              newProduct={newProduct}
              isOpen={isProductModalOpen}
              onClose={() => setIsProductModalOpen(false)}
              onAdd={handleAddProduct} // 🔥 Új termék hozzáadása
              allProducts={allProducts}
              setNewProduct={setNewProduct} // ✅ Ezt mindenképp add át!
              setIsProductModalOpen={setIsProductModalOpen} // ✅ Ezt mindenképp add át!
              existingProducts={editedOrder.orderItems} // ✅ Átadjuk a rendelésben szereplő termékeket
              handleAddProduct={handleAddProduct}
            />
          )}
        </div>
      </div>
    )
  );
};

export default EditOrderModal;
